import type { Artwork, ArtworkWrapper } from "@/types/artworksData";

type AutoPopulateSlotTarget = {
  id: string;
  wrapperId?: string;
  slotTarget: number;
};

type AutoPopulateHydrationPayload = {
  wrappers?: ArtworkWrapper[];
  createdArtworks?: Artwork[];
  slotTargets?: AutoPopulateSlotTarget[];
};

type AutoPopulateHydrationOptions = {
  preserveExistingArtworkObjects?: boolean;
  preserveExistingActiveArtwork?: boolean;
};

function defaultTransforms() {
  return {
    position: { x: 0, y: 0, z: 0 },
    rotation: { x: 0, y: 0, z: 0 },
    scale: { x: 1, y: 1, z: 1 },
  };
}

function mergeTransforms(existing: any, incoming: any) {
  const defaults = defaultTransforms();
  return {
    ...defaults,
    ...existing,
    ...incoming,
    position: {
      ...defaults.position,
      ...(existing?.position || {}),
      ...(incoming?.position || {}),
    },
    rotation: {
      ...defaults.rotation,
      ...(existing?.rotation || {}),
      ...(incoming?.rotation || {}),
    },
    scale: {
      ...defaults.scale,
      ...(existing?.scale || {}),
      ...(incoming?.scale || {}),
    },
  };
}

function setFrameWrapperIds(wrapper: ArtworkWrapper) {
  (wrapper.artworks || []).forEach((artwork: Artwork) => {
    if (artwork?.artworkData?.frame) {
      artwork.artworkData.frame.artworkWrapperID = wrapper._id;
    }
  });
}

function wrapperKey(wrapper: Partial<ArtworkWrapper>) {
  return String(wrapper._id || wrapper.slotTarget);
}

function normalizeArtworkId(value: any): string {
  const candidate = value?._id ?? value;
  if (typeof candidate === "string") return candidate;
  if (typeof candidate?.toHexString === "function") return candidate.toHexString();
  return candidate?.toString?.() || "";
}

export function wrapperContainsArtwork(wrapper: Partial<ArtworkWrapper> | null | undefined, artworkId: string) {
  if (!artworkId || !Array.isArray(wrapper?.artworks)) return false;
  return wrapper.artworks.some((artwork: any) => normalizeArtworkId(artwork) === String(artworkId));
}

export function isInvalidPseudoMoMRemoveResponse(
  existingWrapper: Partial<ArtworkWrapper> | null | undefined,
  responseWrapper: Partial<ArtworkWrapper> | null | undefined
) {
  const existingCount = Array.isArray(existingWrapper?.artworks) ? existingWrapper.artworks.length : 0;
  const responseCount = Array.isArray(responseWrapper?.artworks) ? responseWrapper.artworks.length : 0;
  return existingCount > 1 && responseCount === 0;
}

function shouldUseIncomingArtworks(incoming: Partial<ArtworkWrapper>, existingWrapper?: ArtworkWrapper) {
  if (!Array.isArray(incoming.artworks)) return false;
  if (incoming.artworks.length === 0) return true;
  if (!existingWrapper?.artworks?.length) return true;
  return incoming.artworks.some((artwork: any) => typeof artwork === "object" && artwork !== null);
}

function mergeWrapperIntoGallery(
  artworkWrappers: ArtworkWrapper[],
  incoming: Partial<ArtworkWrapper>,
  galleryId: string,
  options: AutoPopulateHydrationOptions = {}
): ArtworkWrapper {
  const existingWrapper = artworkWrappers.find((wrapper) =>
    String(wrapper._id) === String(incoming._id) ||
    Number(wrapper.slotTarget) === Number(incoming.slotTarget)
  );
  const wrapperToLoad = {
    ...(existingWrapper || {}),
    ...incoming,
    _id: String(incoming._id || existingWrapper?._id || ""),
    galleryBelongsTo: String(incoming.galleryBelongsTo || existingWrapper?.galleryBelongsTo || galleryId),
    slotTarget: Number(incoming.slotTarget ?? existingWrapper?.slotTarget),
    artworks: options.preserveExistingArtworkObjects && existingWrapper?.artworks?.length
      ? existingWrapper.artworks
      : shouldUseIncomingArtworks(incoming, existingWrapper)
      ? incoming.artworks
      : (Array.isArray(existingWrapper?.artworks) ? existingWrapper.artworks : []),
    activeArtwork: options.preserveExistingActiveArtwork && Number.isInteger(existingWrapper?.activeArtwork)
      ? existingWrapper.activeArtwork
      : Number.isInteger(incoming.activeArtwork)
      ? incoming.activeArtwork
      : (existingWrapper?.activeArtwork ?? 0),
    transforms: mergeTransforms(existingWrapper?.transforms, incoming.transforms),
  } as ArtworkWrapper;

  setFrameWrapperIds(wrapperToLoad);

  if (existingWrapper) {
    Object.assign(existingWrapper, wrapperToLoad);
    return existingWrapper;
  }

  artworkWrappers.push(wrapperToLoad);
  return wrapperToLoad;
}

export function hydrateAutoPopulateArtworkWrappers(
  currentWrappers: ArtworkWrapper[],
  data: AutoPopulateHydrationPayload,
  galleryId: string,
  options: AutoPopulateHydrationOptions = {}
): { artworkWrappers: ArtworkWrapper[]; wrappersToLoad: ArtworkWrapper[] } {
  const artworkWrappers = Array.isArray(currentWrappers) ? currentWrappers : [];
  const wrappersToLoad = new Map<string, ArtworkWrapper>();

  if (Array.isArray(data.wrappers) && data.wrappers.length > 0) {
    data.wrappers.forEach((incomingWrapper) => {
      const wrapperToLoad = mergeWrapperIntoGallery(artworkWrappers, incomingWrapper, galleryId, options);
      wrappersToLoad.set(wrapperKey(wrapperToLoad), wrapperToLoad);
    });

    return {
      artworkWrappers,
      wrappersToLoad: Array.from(wrappersToLoad.values()),
    };
  }

  if (Array.isArray(data.createdArtworks) && data.createdArtworks.length > 0) {
    data.createdArtworks.forEach((artwork: Artwork) => {
      const slotTarget = (data.slotTargets || []).find((st) => String(st.id) === String(artwork._id));
      if (!slotTarget) {
        return;
      }

      let wrapperToLoad = artworkWrappers.find((wrapper) =>
        String(wrapper._id) === String(slotTarget.wrapperId) ||
        Number(wrapper.slotTarget) === Number(slotTarget.slotTarget)
      );

      if (!wrapperToLoad) {
        wrapperToLoad = mergeWrapperIntoGallery(artworkWrappers, {
          _id: slotTarget.wrapperId || "",
          galleryBelongsTo: galleryId,
          slotTarget: slotTarget.slotTarget,
          artworks: [],
          activeArtwork: 0,
          transforms: defaultTransforms(),
        } as ArtworkWrapper, galleryId, options);
      } else {
        wrapperToLoad._id = slotTarget.wrapperId || wrapperToLoad._id;
        wrapperToLoad.slotTarget = slotTarget.slotTarget;
        wrapperToLoad.artworks = Array.isArray(wrapperToLoad.artworks) ? wrapperToLoad.artworks : [];
      }

      const existingArtworkIndex = wrapperToLoad.artworks.findIndex((existingArtwork: any) =>
        String(existingArtwork?._id || existingArtwork) === String(artwork._id)
      );
      if (existingArtworkIndex >= 0) {
        wrapperToLoad.artworks[existingArtworkIndex] = artwork;
      } else {
        wrapperToLoad.artworks.push(artwork);
      }
      wrapperToLoad.activeArtwork = wrapperToLoad.artworks.length - 1;
      wrapperToLoad.transforms = mergeTransforms(wrapperToLoad.transforms, wrapperToLoad.transforms);
      setFrameWrapperIds(wrapperToLoad);
      wrappersToLoad.set(wrapperKey(wrapperToLoad), wrapperToLoad);
    });
  }

  return {
    artworkWrappers,
    wrappersToLoad: Array.from(wrappersToLoad.values()),
  };
}
